import { Link, Outlet, rootRouteId, useRouterState } from "@tanstack/react-router";
import { ShieldAlert } from "lucide-react";
import { useDeviceApprovals, useIsSignedIn, useProfile, useSignOut } from "@empite/session";
import type { AccountProfile, SessionInfo } from "@empite/api-client";
import { AppShell } from "../ui/app-shell";
import type { AccountSummary } from "../ui/sidebar";
import { Notice } from "../ui/notice";
import { usePathname } from "../ui/router";
import { canSpend } from "./guards";

/**
 * The authentication screens that own the WHOLE viewport through `AuthShell`.
 *
 * `AuthShell` is itself the frame — its own split layout, its own background —
 * so wrapping it in the dashboard chrome would draw two frames around one
 * screen. `/authorize` is deliberately NOT here: it is a signed-in decision,
 * not a sign-in, and the rail and top bar are how the customer can tell this is
 * their own account being asked.
 */
const FULL_SCREEN_PATHS = ["/sign-in", "/register", "/verify-phone"];

/**
 * Whether the router resolved the current URL to a screen at all.
 *
 * An unmatched path produces exactly one match — the root — and the root's
 * outlet renders its `notFoundComponent`. So the leaf match still being the
 * root IS the not-found state, and it is read from the match list rather than
 * by testing the pathname against the route table a second time: a copy of
 * that list here would drift the moment a route is added.
 *
 * The not-found screen is full-viewport, like the design's `app/not-found.tsx`
 * which sits above every layout group. It carries its own centred layout, so
 * the shell around it would be a second frame — and the shell's rail links are
 * no help on a URL that does not exist.
 */
function useIsNotFound(): boolean {
  return useRouterState({
    select: (state) => state.matches[state.matches.length - 1]?.routeId === rootRouteId,
  });
}

/**
 * Screens that own the WHOLE viewport when there is NO session, and go back
 * inside the dashboard shell when there is.
 *
 * `/` is the only one: signed out it is the design's marketing page, which
 * carries its own header and footer and whose sections run full-bleed, so the
 * padded content column `SignedOutScreen` provides would box them in. Signed in
 * it is the dashboard, which is an ordinary shell screen.
 *
 * A router layout cannot make this call — it cannot see the session — which is
 * why it is decided here and not in `routes.tsx`.
 */
const SIGNED_OUT_FULL_SCREEN_PATHS = ["/"];

/**
 * The shell every screen renders inside.
 *
 * The design has two frames, not one: a signed-in dashboard shell (rail, top
 * bar, one `<main>`) and no frame at all for the authentication screens, which
 * own the whole viewport through `AuthShell`. So this component only decides
 * WHICH frame the route gets — a signed-out visitor is handed the bare outlet
 * rather than a stripped-down copy of the dashboard chrome.
 *
 * Everything the shell displays is passed in as a prop: `src/ui/` holds no
 * session and no query, which is the isolation rule `make ui-isolation-check`
 * enforces.
 */
export function Layout() {
  const { signedIn, session } = useIsSignedIn();
  const pathname = usePathname();
  const notFound = useIsNotFound();

  // An authentication screen is the frame — no shell around it, whether or not
  // a session happens to exist. The not-found screen is drawn the same way, for
  // the same reason.
  if (notFound || FULL_SCREEN_PATHS.includes(pathname)) {
    return <Outlet />;
  }
  // Hooks that need a session live in SignedInShell, so they are never issued
  // for a visitor who has none.
  if (!signedIn || !session) {
    if (SIGNED_OUT_FULL_SCREEN_PATHS.includes(pathname)) {
      return <Outlet />;
    }
    return <SignedOutScreen />;
  }
  return <SignedInShell session={session} />;
}

/**
 * A shell screen reached without a session: the shell's `<main>` column without
 * the shell, so the page still has a gutter while its guard decides whether to
 * redirect.
 */
function SignedOutScreen() {
  return (
    <div className="mx-auto w-full max-w-[1440px] px-4 py-6 sm:px-6 sm:py-8">
      <Outlet />
    </div>
  );
}

/**
 * Initials for the avatar: the first letter of the first two words, so
 * "Demo Customer" reads DC and a single-word name reads its first letter.
 */
function initialsOf(name: string): string {
  const letters = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0] ?? "")
    .join("");
  return letters.toUpperCase() || "?";
}

/**
 * The design read the signed-in account from a fixture with a name and an
 * address on it. The platform's `SessionInfo` carries neither — it is opaque
 * identifiers only — so the profile supplies them.
 *
 * The user id is deliberately NOT the fallback name: initials taken from
 * `01a0390b-…` render as "0", which reads as a broken avatar rather than as an
 * identifier. Until the profile arrives the rail says "Your account".
 */
function accountSummary(profile: AccountProfile | undefined): AccountSummary {
  const emails = profile?.emails ?? [];
  const email = (emails.find((entry) => entry.is_primary) ?? emails[0])?.address ?? "";
  const named = profile?.display_name?.trim() || email.split("@")[0] || "";
  return {
    name: named || "Your account",
    email,
    initials: named ? initialsOf(named) : "?",
  };
}

function SignedInShell({ session }: { session: SessionInfo }) {
  const profile = useProfile();
  const approvals = useDeviceApprovals();
  const signOut = useSignOut();

  const pending = (approvals.data?.device_approvals ?? []).filter(
    (approval) => approval.status === "pending",
  ).length;
  const verified = canSpend(session);

  return (
    <AppShell
      account={accountSummary(profile.data)}
      suiteId={session.suite_id}
      registrationState={session.registration_state}
      // A green pill on an account that cannot spend is a lie; the tone follows
      // the same predicate the spending guard uses.
      registrationTone={verified ? "success" : "warning"}
      pendingCount={pending}
      onSignOut={() => signOut.mutate()}
    >
      {/*
        The old nav carried a "Verify your phone" badge in the header. The
        dashboard shell has no slot for one, so the precondition is stated at
        the top of the content instead — still on every page, still a link to
        the screen that clears it, and still carrying the user id `/verify-phone`
        needs (it works with no cookie at all).
      */}
      {verified ? null : (
        <Notice
          title="Your phone is not verified yet"
          icon={<ShieldAlert className="size-4" aria-hidden />}
          className="mb-6"
        >
          You cannot activate an application or spend credit until your phone number is
          verified.{" "}
          <Link
            to="/verify-phone"
            search={{ user_id: session.user_id, next: undefined }}
            className="font-semibold text-brand hover:text-brand-strong"
          >
            Verify your phone
          </Link>
          .
        </Notice>
      )}
      <Outlet />
    </AppShell>
  );
}
